import { useEffect, useState, type FormEvent } from 'react';
import {
  Copy,
  Check,
  Save,
  Webhook,
  CreditCard,
  CheckCircle2,
  ExternalLink,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePlatformBilling, useSavePlatformBilling } from '@/hooks/useBilling';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';

export function BillingSettingsPage() {
  const { data, isPending } = usePlatformBilling();
  const save = useSavePlatformBilling();

  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [currency, setCurrency] = useState<'try' | 'usd' | 'eur'>('try');
  const [enabled, setEnabled] = useState(false);
  const [successUrl, setSuccessUrl] = useState('');
  const [cancelUrl, setCancelUrl] = useState('');
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.stripe) {
      setCurrency((data.stripe.currency as 'try' | 'usd' | 'eur') ?? 'try');
      setEnabled(data.stripe.enabled);
      setPublishableKey(data.stripe.publishable_key ?? '');
    }
  }, [data]);

  const onCopyWebhook = async () => {
    if (!data?.webhook_url) return;
    await navigator.clipboard.writeText(data.webhook_url);
    setCopiedWebhook(true);
    setTimeout(() => setCopiedWebhook(false), 2000);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await save.mutateAsync({
        secret_key: secretKey || undefined,
        webhook_secret: webhookSecret || undefined,
        publishable_key: publishableKey,
        currency,
        success_url: successUrl || undefined,
        cancel_url: cancelUrl || undefined,
        enabled,
      });
      setSecretKey('');
      setWebhookSecret('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    }
  };

  if (isPending || !data) return <div className="text-muted-foreground">Yükleniyor…</div>;

  const s = data.stripe;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <CreditCard className="w-5 h-5" /> Webhook & Billing
        </h1>
        <p className="text-muted-foreground text-sm">
          Stripe ödeme entegrasyonu. Secret key ve webhook secret AES-GCM ile şifrelenmiş olarak
          D1'de saklanır.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Mevcut Durum
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Row label="Secret Key" value={s.configured ? s.key_hint ?? '—' : 'Yapılandırılmamış'} ok={s.configured} />
          <Row label="Webhook Secret" value={s.webhook_secret_set ? '✓ Kayıtlı' : 'Yok'} ok={s.webhook_secret_set} />
          <Row label="Para Birimi" value={s.currency.toUpperCase()} ok />
          <Row
            label="Entegrasyon"
            value={s.enabled ? 'Aktif' : 'Kapalı'}
            ok={s.enabled}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Webhook className="w-4 h-4" /> Webhook URL
          </CardTitle>
          <CardDescription>
            Stripe Dashboard → Developers → Webhooks → Add endpoint'e bu URL'i gir. Event seç:{' '}
            <code className="px-1 py-0.5 bg-muted rounded text-[11px]">checkout.session.completed</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex gap-2 items-center">
            <code className="flex-1 min-w-0 font-mono text-xs bg-muted px-2 py-1.5 rounded truncate">
              {data.webhook_url}
            </code>
            <Button size="sm" variant="outline" type="button" onClick={onCopyWebhook}>
              {copiedWebhook ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Webhook oluşturduktan sonra Stripe'ın verdiği "Signing secret"i (wh_sec_…) aşağıya yapıştır.
          </p>
        </CardContent>
      </Card>

      <form onSubmit={onSubmit} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Stripe API Anahtarları</CardTitle>
            <CardDescription>
              <a
                href="https://dashboard.stripe.com/apikeys"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                dashboard.stripe.com/apikeys <ExternalLink className="w-3 h-3" />
              </a>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sk">
                Secret Key {s.configured && <span className="text-xs text-muted-foreground">(yenilemek için yaz)</span>}
              </Label>
              <Input
                id="sk"
                type="password"
                autoComplete="off"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder={s.configured ? '••••••••••••••••' : 'sk_test_… / sk_live_…'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ws">
                Webhook Signing Secret{' '}
                {s.webhook_secret_set && <span className="text-xs text-muted-foreground">(yenilemek için yaz)</span>}
              </Label>
              <Input
                id="ws"
                type="password"
                autoComplete="off"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder="whsec_…"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pk">Publishable Key (opsiyonel, frontend için)</Label>
              <Input
                id="pk"
                value={publishableKey}
                onChange={(e) => setPublishableKey(e.target.value)}
                placeholder="pk_test_… / pk_live_…"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Yapılandırma</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cur">Para Birimi</Label>
              <select
                id="cur"
                value={currency}
                onChange={(e) => setCurrency(e.target.value as 'try' | 'usd' | 'eur')}
                className="flex h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="try">₺ TRY (Türk Lirası)</option>
                <option value="usd">$ USD (ABD Doları)</option>
                <option value="eur">€ EUR (Euro)</option>
              </select>
              <p className="text-xs text-muted-foreground">
                Paket tablosundaki fiyat sütunu bu para birimine göre okunur.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="su">Başarı URL (opsiyonel)</Label>
                <Input
                  id="su"
                  value={successUrl}
                  onChange={(e) => setSuccessUrl(e.target.value)}
                  placeholder="default: /packages/thank-you?order={id}"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cu">İptal URL (opsiyonel)</Label>
                <Input
                  id="cu"
                  value={cancelUrl}
                  onChange={(e) => setCancelUrl(e.target.value)}
                  placeholder="default: /packages"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="enabled"
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              <Label htmlFor="enabled" className="cursor-pointer">
                Ödeme entegrasyonunu aktif et (kullanıcılar paket satın alabilir)
              </Label>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            {error}
          </div>
        )}
        {saved && (
          <div className="text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 rounded-md px-3 py-2">
            Kaydedildi ✓
          </div>
        )}
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Kaydet
        </Button>
      </form>
    </div>
  );
}

function Row({ label, value, ok }: { label: string; value: string; ok?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b last:border-b-0 py-1.5">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('font-mono', ok ? 'text-foreground' : 'text-muted-foreground')}>{value}</span>
    </div>
  );
}
